// Auth via the BFF (#248, ADR 0047/0055). The browser holds no token and no
// secret — a `__Host-bff` httpOnly cookie is the whole session. This module
// just asks the BFF who the visitor is and drives the browser to its
// `/auth/*` endpoints.

export interface Me {
  sub: string;
  name: string;
  email?: string;
  roles: string[];
  // Set when this session is an admin impersonating this customer (#261,
  // ADR 0062) — the admin's own sub. Absent for an ordinary login.
  impersonator?: string;
}

export type SessionState =
  | { kind: "signed-in"; me: Me }
  | { kind: "signed-out" }
  // The BFF has no Auth0 tenant configured (a fork before docs/auth0-setup.md).
  | { kind: "no-auth" }
  // Couldn't reach the BFF at all (local `vite dev` with no proxy target up).
  | { kind: "unreachable" };

/** One call on load: is there a session, and is auth even set up here? */
export async function loadSession(): Promise<SessionState> {
  let configured = true;
  try {
    const cfg = await fetch("/auth/config", { credentials: "include" });
    if (cfg.ok) {
      const body = (await cfg.json()) as { authConfigured?: boolean };
      configured = body.authConfigured !== false;
    }
  } catch {
    return { kind: "unreachable" };
  }

  try {
    const res = await fetch("/auth/me", { credentials: "include" });
    if (res.ok) {
      return { kind: "signed-in", me: (await res.json()) as Me };
    }
  } catch {
    return { kind: "unreachable" };
  }

  return configured ? { kind: "signed-out" } : { kind: "no-auth" };
}

export function signIn(returnUrl = "/"): void {
  window.location.assign(`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`);
}

export function signOut(returnUrl = "/"): void {
  window.location.assign(`/auth/logout?returnUrl=${encodeURIComponent(returnUrl)}`);
}

/**
 * One-click exit from an impersonated session (#261, ADR 0062). This is a
 * locally-minted session, not an OIDC one — no Auth0-side round trip, just
 * drop the cookie (`/auth/exit-impersonation`, unlike `signOut`'s Auth0
 * logout).
 */
export function exitImpersonation(): void {
  window.location.assign("/auth/exit-impersonation");
}
